import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import type { MeResponse, PublicItem, Reveal as RevealData } from '@shared/types';
import { pick, revealCopy } from '@/lib/kudos';
import { isSinglePick, payloadOf, type AnswerPayload } from '@/lib/answer';
import { useGame, boardAt, type MyAnswer } from '@/store/game';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import { AppHeader } from '@/components/AppHeader';
import { Pulse } from '@/brand/Pulse';
import { MascotSlot } from '@/brand/MascotSlot';
import { ConfettiBurst, CountUp, ScreenFlash, Shake, Wash } from '@/motion';
import { FormatView } from '@/formats';
import { answerKind, creditFor, isCredit, rarityPoints, type Credit } from '@/formats/credit';
import { Screen } from './parts/Screen';
import { Whiteboard, StreakChip } from './parts/Whiteboard';
import { RoomStamp } from './parts/RoomStamp';
import { NextBar } from './parts/NextBar';
import { COPY } from './parts/copy';
import { useOwnSnapshot, isSameReveal } from './parts/frozen';
import { originOf } from './parts/moments';
import { isAnswered, myResultFor, revealContextFor, roomCorrectPct, type MyResult } from './parts/kudosData';
import { useCalm } from './parts/time';

const CONFETTI = ['var(--brand)', 'var(--lime)', 'var(--iv)', 'var(--cart)', 'var(--mark-bolt)'];
/** The mascot's box beside the headline, in px. */
const SLOT = 60;

/**
 * The moment's timings, in ms from the reveal. Everything that makes the moment lands inside 1.5 s, so the answer and
 * the reason are readable for the rest of the reveal (about 7 s): the wash floods at once and has left the screen by
 * 1.4 s; the headline springs in from small; the points land a beat later and count up for 0.9 s; the confetti is
 * still falling but off the words.
 */
export const REVEAL_FX = { wash: 1400, washWrong: 1000, washPartial: 1000, points: 120, count: 900, confetti: 140, confettiLife: [1.6, 2.6] as [number, number] } as const;

/** Short, in the registrar's voice, beside the +0: the player stays in the game. Never a punchline at their expense. */
export const RALLY = ['Still in it.', 'Next one counts.', 'On to the next.'] as const;

/**
 * Partial credit (number, order, pick all): some of the points, and the streak ends. Its own words, since it is
 * neither a win nor a miss. The headline says how close, the points say how much and the answer area spells out the
 * slip (your number beside the answer, the swap, what you found), so the line only reacts.
 */
const PARTIAL: Record<'number' | 'order' | 'multi', { headline: string; lines: readonly string[] }> = {
  number: { headline: 'Close', lines: ['Pharmacy would still query that one.', 'Check your working against the answer below.'] },
  order: { headline: 'Nearly', lines: ['Algorithms are strict. This one let you off lightly.', 'The algorithm forgives one slip. Just the one.'] },
  multi: { headline: 'Partly right', lines: ['Half a differential is still a differential.', 'A good start on the list. Not the whole list.'] },
};

/**
 * A question that scores nothing (the host skipped it, or voided it after the close): said plainly, with no verdict, no
 * points and no key, and without saying whose doing it was. The server steps over it for streaks, so the line says so.
 */
export const VOIDED = { headline: "This question doesn't count", line: 'Nobody scores on this one, and your streak stays as it was.' } as const;
/** The answer was locked in here, but the server stayed too busy to take it before the close: no blame on the signal. */
export const LOST_BUSY = 'Your answer did not reach us before the close. The next one is a fresh start.';

type Graded = { myAnswer?: MyAnswer; me?: MeResponse };
/**
 * Number, order and pick-all answers land in a credit bucket. The reveal carries the true value, order or options, so
 * this phone works the bucket out at once and the moment does not wait for /me; the server's own record wins as soon
 * as it arrives (a record read while the question was open comes without its bucket, so it is graded the same way).
 */
function gradeLocally(q: Pick<PublicItem, 'format' | 'multi' | 'number' | 'maxPoints' | 'multiplier'>, r: RevealData, index: number, myAnswer?: MyAnswer, me?: MeResponse): Graded {
  if (answerKind(q) !== 'credit') return { myAnswer, me };
  const grade = <T extends { optionId: string }>(a: T): T => {
    if (isCredit(a.optionId)) return a;
    const c = creditFor(q, r, payloadOf(a as Partial<AnswerPayload>));
    return c ? { ...a, optionId: c } : a;
  };
  const key = `q${index}`;
  const rec = me?.answers?.[key];
  return {
    myAnswer: myAnswer && myAnswer.index === index ? grade(myAnswer) : myAnswer,
    me: me && rec && typeof rec.correct !== 'boolean' ? { ...me, answers: { ...me.answers, [key]: grade(rec) } } : me,
  };
}

/** M6 to M8: the answer view. Right, wrong or silent (or partly right); the points; a line on the whiteboard; the room's split; why. */
export default function Reveal() {
  const snap = useOwnSnapshot(isSameReveal)!;
  const session = useGame((s) => s.session); const me = useGame((s) => s.me);
  const myAnswer = useGame((s) => s.myAnswer); const seenQuestion = useGame((s) => s.seenQuestion); const revealSeen = useGame((s) => s.revealSeen);
  const reduce = useCalm();
  const formatRef = useRef<HTMLDivElement>(null);
  const r = snap.reveal; const q = snap.question;
  const playerId = session?.playerId;
  const voided = Boolean(r?.voided);

  const graded = useMemo(() => (r && q ? gradeLocally(q, r, snap.index, myAnswer, me) : { myAnswer, me }), [r, q, snap.index, myAnswer, me]);
  const result: MyResult = r && q ? myResultFor({ index: snap.index, reveal: r, startsAt: q.startsAt, myAnswer: graded.myAnswer, me: graded.me, playerId, seenQuestion }) : { kind: 'pending' };
  const ctx = r && q ? revealContextFor({ playerId: playerId ?? '', index: snap.index, result, durationMs: q.durationMs, answers: me?.playerId === playerId ? me?.answers : undefined, reveal: r, me }) : undefined;
  const pct = r ? roomCorrectPct(r) : undefined;

  // The whole answer this player gave, for the answer area: the server's record when it has one, else this phone's lock-in.
  const mine: AnswerPayload | undefined = useMemo(() => {
    const rec = me && playerId && me.playerId === playerId ? me.answers?.[`q${snap.index}`] : undefined;
    if (rec) return payloadOf(rec);
    return myAnswer && myAnswer.index === snap.index ? payloadOf(myAnswer) : undefined;
  }, [me, playerId, myAnswer, snap.index]);
  const credit: Credit | undefined = q && r && answerKind(q) === 'credit' && isAnswered(result)
    ? (isCredit(result.optionId) ? result.optionId : creditFor(q, r, mine)) : undefined;
  const partial = result.kind === 'wrong' && credit === 'partial';
  const partialCopy = partial && q ? PARTIAL[q.format === 'number' ? 'number' : q.format === 'order' ? 'order' : 'multi'] : undefined;
  const copy = ctx ? revealCopy(ctx) : undefined;
  const moment: 'right' | 'partial' | 'wrong' | 'none' = result.kind === 'right' ? 'right' : partial ? 'partial' : result.kind === 'wrong' ? 'wrong' : 'none';

  // The moment fires once per question, as soon as this phone knows how it went.
  const fired = useRef(-1);
  const [fx, setFx] = useState<{ index: number; kind: typeof moment; origin?: { x: number; y: number } }>();
  const single = q ? isSinglePick(q) : true;
  const myOptionId = single && (result.kind === 'right' || result.kind === 'wrong' || result.kind === 'lost') ? result.optionId : undefined;
  useEffect(() => {
    if (!r || voided || result.kind === 'pending' || fired.current === snap.index) return;
    fired.current = snap.index;
    if (moment === 'right') { sfx.play('correct'); haptic('success'); }
    else if (moment === 'partial') { sfx.play('ping'); haptic('tap'); }
    else if (moment === 'wrong') { sfx.play('wrong'); haptic('fail'); }
    setFx({ index: snap.index, kind: moment, origin: originOf(formatRef.current, myOptionId && moment === 'right' ? myOptionId : r.correctId) });
  }, [snap.index, result.kind, moment, voided]); // eslint-disable-line react-hooks/exhaustive-deps
  const streakOn = fx?.kind === 'right' && Boolean(copy?.chip);
  useEffect(() => {
    if (!streakOn) return;
    const t = window.setTimeout(() => sfx.play('streak'), 650);
    return () => window.clearTimeout(t);
  }, [streakOn]);

  if (!r || !q) return <Screen center><p className="text-center text-ink-2">{COPY.question.expired}</p></Screen>;
  const switchAt = boardAt(snap, revealSeen?.index === snap.index ? revealSeen.at : undefined);

  if (voided) {
    // No key, split or reason: on a wrongly keyed question they would teach the wrong answer. What was asked, and what this player chose, stay.
    return (
      <Screen phase="reveal" label={COPY.question.of(snap.index, snap.total)} header={<AppHeader progress={{ index: snap.index, total: snap.total }} />}>
        <section aria-live="polite" aria-atomic="true" className="flex flex-col gap-2" data-part="voided">
          <h1 className="display min-w-0 text-[2.6rem] leading-[0.95] text-ink">{VOIDED.headline}</h1>
          <div className="text-ink-3"><Pulse mood="steady" width="100%" height={40} /></div>
        </section>
        <Whiteboard line={VOIDED.line} tone="quiet" />
        <FormatView question={q} phase="locked" myOptionId={single ? mine?.optionId : undefined} myAnswer={mine} display={snap.display} disabled />
        {switchAt !== undefined && <NextBar from={switchAt - (snap.revealMs ?? 7000)} to={switchAt} label={COPY.reveal.boardIn} tone="ink" />}
      </Screen>
    );
  }

  const known = result.kind !== 'pending';
  const seed = `${playerId ?? ''}:${snap.index}:partial`;
  const headline = result.kind === 'pending' ? COPY.reveal.pending : result.kind === 'lost' ? COPY.reveal.lostHeadline : partialCopy ? partialCopy.headline : copy!.headline;
  const lostLine = graded.myAnswer?.index === snap.index && graded.myAnswer.undelivered === 'busy' ? LOST_BUSY : COPY.reveal.lostLine;
  const line = result.kind === 'pending' ? COPY.reveal.checking : result.kind === 'lost' ? lostLine : partialCopy ? pick(partialCopy.lines, seed) : copy!.line;
  const tone = moment === 'right' ? 'text-correct' : moment === 'partial' ? 'text-cart-strong' : moment === 'wrong' ? 'text-wrong' : 'text-ink';
  // A right rarity pick is paid at the close, by how few chose it: the reveal carries what it takes to work it out.
  const serverPoints = isAnswered(result) ? result.points : undefined;
  const rarity = q.format === 'multi' && q.multi?.mode === 'rarity' && result.kind === 'right' ? rarityPoints(q, r, result.optionId) : undefined;
  const points = rarity !== undefined ? Math.max(serverPoints ?? 0, rarity) : serverPoints;
  const fire = fx && fx.index === snap.index ? fx : undefined;
  const chipLost = Boolean(copy?.chip) && result.kind !== 'right';
  const rally = pick(RALLY, `${playerId ?? ''}:${snap.index}:rally`);

  return (
    <Screen phase="reveal" label={COPY.question.of(snap.index, snap.total)} header={<AppHeader progress={{ index: snap.index, total: snap.total }} />}>
      <section aria-live="polite" aria-atomic="true" className="flex flex-col gap-3">
        <Shake trigger={fire?.kind === 'wrong' ? `wrong-${snap.index}` : undefined}>
          <div className="flex items-center gap-3">
            {/* The mascot's box: the Pulse in the mood of the moment for now; a steady line while the answer is still being checked. */}
            {known
              ? <MascotSlot moment={moment} size={SLOT} />
              : <span aria-hidden="true" className="inline-flex shrink-0 items-center justify-center text-ink-3" style={{ width: SLOT, height: SLOT }}><Pulse mood="steady" width={SLOT} height={Math.round(SLOT * 0.42)} /></span>}
            <motion.h1 key={`${snap.index}-${result.kind}-${moment}`} initial={reduce || !known ? { opacity: 0 } : { opacity: 0, scale: 0.5, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={reduce || !known ? { duration: 0.2 } : { type: 'spring', stiffness: 380, damping: 14, mass: 0.9 }}
              className={`display min-w-0 flex-1 origin-left text-[3rem] leading-[0.92] ${tone}`}>
              {headline}
            </motion.h1>
          </div>
        </Shake>
        {(moment === 'right' || moment === 'partial') && (
          <motion.p key={`points-${snap.index}-${moment}`} data-part="points" initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.4, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={reduce ? { duration: 0.2 } : { delay: REVEAL_FX.points / 1000, type: 'spring', stiffness: 420, damping: 15 }}
            className="flex origin-left items-center">
            {points === undefined
              ? <span className={`numeral inline-flex rounded-tile px-4 py-1 text-5xl text-ink-3 ${moment === 'right' ? 'bg-lime/60' : 'bg-cart-soft/70'}`} aria-label="Points on the way">+…</span>
              : <span className={`inline-flex rounded-tile px-4 py-1 ${moment === 'right' ? 'bg-lime' : 'bg-cart-soft'}`}><CountUp value={points} from={0} durationMs={REVEAL_FX.count} prefix="+" className="text-5xl text-ink" /></span>}
          </motion.p>
        )}
        {moment === 'wrong' && (
          <p className="flex items-baseline gap-3">
            <span className="numeral text-3xl text-ink-3">+0</span>
            <span data-part="rally" className="text-base font-semibold text-ink-2">{rally}</span>
          </p>
        )}
      </section>

      {known ? (
        <Whiteboard line={line} tone={moment === 'right' ? 'right' : moment === 'wrong' ? 'wrong' : 'quiet'}>
          {copy?.chip && ctx && <StreakChip text={copy.chip} n={chipLost ? ctx.streakBefore : ctx.streakAfter} lost={chipLost} />}
        </Whiteboard>
      ) : (
        <p className="text-ink-2">{line}</p>
      )}

      <RoomStamp pct={pct} />

      <div ref={formatRef}>
        <FormatView question={q} phase="revealed" reveal={r} myOptionId={myOptionId} myAnswer={mine} display={snap.display} disabled />
      </div>

      {r.explanation && (
        <section className="rounded-tile border border-line bg-surface px-4 py-3">
          <h2 className="font-display text-base font-extrabold text-ink">{COPY.reveal.why}</h2>
          <p className="mt-1 leading-relaxed text-ink-2">{r.explanation}</p>
        </section>
      )}
      <p className="text-center text-xs text-ink-3">{COPY.reveal.answered(r.answered, Math.max(r.answered, snap.playerCount))}</p>
      {switchAt !== undefined && <NextBar from={switchAt - (snap.revealMs ?? 7000)} to={switchAt} label={COPY.reveal.boardIn} tone="ink" />}

      {fire?.kind === 'right' && (
        <>
          <ScreenFlash trigger={`right-${snap.index}`} color="var(--correct)" />
          <Wash trigger={`right-${snap.index}`} color="var(--correct)" peak={0.4} durationMs={REVEAL_FX.wash} />
          <ConfettiBurst trigger={`right-${snap.index}`} origin={fire.origin ?? 'center'} colors={CONFETTI} count={REVEAL_FX.confetti} life={REVEAL_FX.confettiLife} />
        </>
      )}
      {fire?.kind === 'wrong' && (
        <>
          <ScreenFlash trigger={`wrong-${snap.index}`} color="var(--wrong-soft)" durationMs={420} />
          <Wash trigger={`wrong-${snap.index}`} color="var(--wrong-soft)" peak={0.62} durationMs={REVEAL_FX.washWrong} />
        </>
      )}
      {fire?.kind === 'partial' && <Wash trigger={`partial-${snap.index}`} color="var(--cart-soft)" peak={0.42} durationMs={REVEAL_FX.washPartial} />}
    </Screen>
  );
}
